import React, { useEffect, useState, useMemo } from 'react';
import { Table, Button, Modal, Form, Input, InputNumber, Select, Upload, Popconfirm, message, Space, Card, Row, Col, Collapse, Switch, Statistic, Radio, Grid } from 'antd';
import { PlusOutlined, DownloadOutlined, UploadOutlined, EditOutlined, DeleteOutlined, SearchOutlined, FilterOutlined, ClearOutlined, WarningOutlined } from '@ant-design/icons';
import { useSearchParams } from 'react-router-dom';
import ExcelJS from 'exceljs';
import { malzemelerAPI, malzemeBirimDonusumleriAPI } from '../services/api';
import { useLookups } from '../hooks/useLookups';
import MalzemeDetayModal from '../components/MalzemeDetayModal';
import BirimDonusumModal from '../components/BirimDonusumModal';
import { paraInputProps, formatPara, formatSayi } from '../utils/format';
import { bosSecenekliOptions, secimeUyuyor } from '../utils/filtre';

function Malzemeler() {
  const screens = Grid.useBreakpoint();
  const isMobile = !screens.md;
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [birimKilitli, setBirimKilitli] = useState(false);
  const [birimKullanim, setBirimKullanim] = useState(null);
  const [aramaMetni, setAramaMetni] = useState('');
  const [filtreBirimIds, setFiltreBirimIds] = useState([]);
  const [filtreAdIds, setFiltreAdIds] = useState([]);
  const [filtreFiyatOp, setFiltreFiyatOp] = useState(null);
  const [filtreFiyatDeger, setFiltreFiyatDeger] = useState(null);
  const [filtreDonusum, setFiltreDonusum] = useState('tumu');
  const [donusumluMalzemeIds, setDonusumluMalzemeIds] = useState(new Set());
  const [filtreAcik, setFiltreAcik] = useState([]);
  const [detayOpen, setDetayOpen] = useState(false);
  const [detayMalzeme, setDetayMalzeme] = useState(null);
  const [donusumOpen, setDonusumOpen] = useState(false);
  const [donusumMalzeme, setDonusumMalzeme] = useState(null);
  const [form] = Form.useForm();
  const [searchParams, setSearchParams] = useSearchParams();

  const { malzemeTipiOptions: malzemeTipleri, birimOptions: birimler, birimler: birimlerRaw } = useLookups();

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await malzemelerAPI.getAll();
      setData(res.data);
    } catch (err) {
      message.error('Malzemeler yüklenemedi: ' + (err.response?.data?.hata || err.message));
    } finally {
      setLoading(false);
    }
  };

  const fetchDonusumluMalzemeler = async () => {
    try {
      const res = await malzemeBirimDonusumleriAPI.getAllSon();
      setDonusumluMalzemeIds(new Set((res.data || []).map(d => d.malzeme_id)));
    } catch {
      setDonusumluMalzemeIds(new Set());
    }
  };

  useEffect(() => {
    fetchData();
    fetchDonusumluMalzemeler();
  }, []);

  useEffect(() => {
    const yeniMalzeme = searchParams.get('yeni');
    if (yeniMalzeme) {
      form.setFieldsValue({ ad: yeniMalzeme });
      setModalOpen(true);
      setSearchParams({}, { replace: true });
    }
  }, [searchParams]);

  const handleSubmit = async (values) => {
    try {
      if (editingRecord) {
        await malzemelerAPI.update(editingRecord.id, values);
        message.success('Malzeme güncellendi');
      } else {
        await malzemelerAPI.create(values);
        message.success('Malzeme eklendi');
      }
      setModalOpen(false);
      setEditingRecord(null);
      form.resetFields();
      fetchData();
    } catch (err) {
      message.error((editingRecord ? 'Malzeme güncellenemedi: ' : 'Malzeme eklenemedi: ') + (err.response?.data?.hata || err.message));
    }
  };

  const handleEdit = async (record) => {
    setEditingRecord(record);
    form.setFieldsValue({
      ad: record.ad,
      aciklama: record.aciklama,
      birim_id: record.birim_id,
    });
    setBirimKilitli(false);
    setBirimKullanim(null);
    setModalOpen(true);
    try {
      const res = await malzemelerAPI.getKullanim(record.id);
      if (res.data?.toplam > 0) {
        setBirimKilitli(true);
        setBirimKullanim(res.data);
      }
    } catch {
      // sessiz geç — backend yine de koruyor
    }
  };

  const handleDelete = async (id) => {
    try {
      await malzemelerAPI.remove(id);
      message.success('Malzeme silindi');
      fetchData();
    } catch (err) {
      message.error('Malzeme silinemedi: ' + (err.response?.data?.hata || err.message));
    }
  };

  const handleSablonIndir = async () => {
    const workbook = new ExcelJS.Workbook();
    const sheet = workbook.addWorksheet('Malzemeler');

    sheet.columns = [
      { header: 'Ad', key: 'ad', width: 25 },
      { header: 'Açıklama', key: 'aciklama', width: 30 },
      { header: 'Birim (1=kg, 2=gr, 3=lt, 4=ml, 5=adet, 6=porsiyon, 7=paket)', key: 'birim_id', width: 50 },
    ];

    sheet.getRow(1).eachCell((cell) => {
      cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF1890FF' } };
      cell.alignment = { vertical: 'middle', wrapText: true };
    });
    sheet.getRow(1).height = 35;

    sheet.addRow({ ad: 'Örnek Malzeme', aciklama: 'Açıklama metni', birim_id: 1 });

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'malzeme_sablonu.xlsx';
    link.click();
    window.URL.revokeObjectURL(url);
  };

  const handleExcelYukle = async (file) => {
    try {
      const workbook = new ExcelJS.Workbook();
      const buffer = await file.arrayBuffer();
      await workbook.xlsx.load(buffer);
      const sheet = workbook.getWorksheet(1);

      const satirlar = [];
      sheet.eachRow((row, rowNumber) => {
        if (rowNumber === 1) return;
        const ad = row.getCell(1).value;
        if (!ad || (typeof ad === 'string' && ad.trim() === '') || ad === 'Örnek Malzeme') return;
        satirlar.push({
          ad: typeof ad === 'string' ? ad.trim() : ad,
          aciklama: row.getCell(2).value || null,
          birim_id: row.getCell(3).value || null,
        });
      });

      if (satirlar.length === 0) {
        message.warning('Excel dosyasında geçerli veri bulunamadı');
        return false;
      }

      let basarili = 0;
      let hatali = 0;
      for (const satir of satirlar) {
        try {
          await malzemelerAPI.create(satir);
          basarili++;
        } catch {
          hatali++;
        }
      }

      if (hatali > 0) {
        message.warning(`${basarili} malzeme eklendi, ${hatali} satırda hata oluştu`);
      } else {
        message.success(`${basarili} malzeme başarıyla eklendi`);
      }
      fetchData();
    } catch {
      message.error('Excel dosyası okunamadı');
    }
    return false;
  };

  const karsilastir = (val, op, deger) => {
    const v = Number(val || 0);
    const d = Number(deger);
    if (op === 'gt')  return v > d;
    if (op === 'gte') return v >= d;
    if (op === 'lt')  return v < d;
    if (op === 'lte') return v <= d;
    if (op === 'eq')  return v === d;
    return true;
  };

  const filteredData = useMemo(() => {
    return data.filter(m => {
      if (aramaMetni) {
        const q = aramaMetni.toLowerCase();
        if (!(
          (m.ad || '').toLowerCase().includes(q) ||
          (m.marka || '').toLowerCase().includes(q) ||
          (m.aciklama || '').toLowerCase().includes(q)
        )) return false;
      }
      if (!secimeUyuyor(filtreBirimIds, m.birim_id)) return false;
      if (filtreAdIds.length > 0 && !filtreAdIds.includes(m.id)) return false;
      if (filtreFiyatOp && filtreFiyatDeger != null && !karsilastir(m.birim_fiyat, filtreFiyatOp, filtreFiyatDeger)) return false;
      if (filtreDonusum === 'var' && !donusumluMalzemeIds.has(m.id)) return false;
      if (filtreDonusum === 'yok' && donusumluMalzemeIds.has(m.id)) return false;
      return true;
    });
  }, [data, aramaMetni, filtreBirimIds, filtreAdIds, filtreFiyatOp, filtreFiyatDeger, filtreDonusum, donusumluMalzemeIds]);

  const filtreAktifMi = filtreBirimIds.length > 0 || filtreAdIds.length > 0 || filtreFiyatOp || filtreDonusum !== 'tumu';

  const filtreTemizle = () => {
    setFiltreBirimIds([]);
    setFiltreAdIds([]);
    setFiltreFiyatOp(null);
    setFiltreFiyatDeger(null);
    setFiltreDonusum('tumu');
  };

  const malzemeAdOptions = useMemo(() => {
    return [...data]
      .sort((a, b) => (a.ad || '').localeCompare(b.ad || '', 'tr'))
      .map(m => ({ value: m.id, label: m.marka && m.marka !== 'markasız' ? `${m.ad} (${m.marka})` : m.ad }));
  }, [data]);

  const birimFilterOptions = useMemo(
    () => bosSecenekliOptions(birimler, data, 'birim_id'),
    [birimler, data]
  );

  const toplamlar = useMemo(() => {
    return { adet: filteredData.length };
  }, [filteredData]);

  const karsilastirmaOpsiyonlari = [
    { value: 'gt', label: '>' }, { value: 'gte', label: '>=' },
    { value: 'lt', label: '<' }, { value: 'lte', label: '<=' },
    { value: 'eq', label: '=' }
  ];

  const strSorter = (key) => (a, b) => (a[key] || '').localeCompare(b[key] || '', 'tr');
  const numSorter = (key) => (a, b) => Number(a[key] || 0) - Number(b[key] || 0);

  const columns = [
    { title: 'Ad', dataIndex: 'ad', key: 'ad', sorter: strSorter('ad'),
      render: (text, record) => (
        <a onClick={() => { setDetayMalzeme(record); setDetayOpen(true); }}>{text}</a>
      ),
    },
    { title: 'Birim', dataIndex: 'birim', key: 'birim', sorter: strSorter('birim'), responsive: ['sm'] },
    { title: 'Birim Fiyat', dataIndex: 'birim_fiyat', key: 'birim_fiyat', sorter: numSorter('birim_fiyat'), render: (v) => v != null ? formatPara(v) : '-' },
    { title: 'Açıklama', dataIndex: 'aciklama', key: 'aciklama', sorter: strSorter('aciklama'), responsive: ['lg'] },
    {
      title: 'İşlem', key: 'islem',
      render: (_, record) => (
        <Space size={4}>
          <Button size="small" icon={<EditOutlined />} onClick={() => handleEdit(record)} />
          <Popconfirm title="Bu malzemeyi silmek istediğinize emin misiniz?" onConfirm={() => handleDelete(record.id)} okText="Evet" cancelText="Hayır">
            <Button danger size="small" icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      )
    }
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, marginBottom: 16, flexWrap: 'nowrap' }}>
        <h2 style={{
          margin: 0,
          fontSize: isMobile ? 16 : 24,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          minWidth: 0,
        }}>Malzemeler</h2>
        <Space size={6} style={{ flexShrink: 0 }}>
          <Input
            placeholder="Ara..."
            prefix={<SearchOutlined />}
            allowClear
            onChange={(e) => setAramaMetni(e.target.value)}
            style={{ width: isMobile ? 110 : 200 }}
          />
          <Button
            icon={<FilterOutlined />}
            onClick={() => setFiltreAcik(filtreAcik.length ? [] : ['1'])}
            type={filtreAktifMi ? 'primary' : 'default'}
            ghost={filtreAktifMi}
            title={filtreAktifMi ? 'Filtreler (Aktif)' : 'Filtreler'}
          >
            {!isMobile && `Filtreler${filtreAktifMi ? ' (Aktif)' : ''}`}
          </Button>
          {screens.md && (
            <>
              <Button icon={<DownloadOutlined />} onClick={handleSablonIndir}>
                {screens.lg ? 'Excel Şablon İndir' : null}
              </Button>
              <Upload accept=".xlsx,.xls" showUploadList={false} beforeUpload={handleExcelYukle}>
                <Button icon={<UploadOutlined />}>
                  {screens.lg ? 'Excel Yükle' : null}
                </Button>
              </Upload>
            </>
          )}
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setModalOpen(true)}
            title="Yeni Malzeme"
          >
            {!isMobile && 'Yeni Malzeme'}
          </Button>
        </Space>
      </div>

      <Collapse activeKey={filtreAcik} onChange={setFiltreAcik} ghost items={[{ key: '1', showArrow: false, header: null, children: (
        <Card size="small" style={{ marginBottom: 16 }} title={<span><FilterOutlined /> Akıllı Filtreler</span>} extra={filtreAktifMi && <Button size="small" icon={<ClearOutlined />} onClick={filtreTemizle}>Temizle</Button>}>
          <Row gutter={[16, 12]}>
            <Col xs={24} sm={12} md={8}>
              <label style={{ fontSize: 12, color: '#666', display: 'block', marginBottom: 4 }}>Malzeme Adı</label>
              <Select mode="multiple" style={{ width: '100%' }} placeholder="Malzeme seçin..." options={malzemeAdOptions} value={filtreAdIds} onChange={setFiltreAdIds} showSearch optionFilterProp="label" maxTagCount={2} allowClear />
            </Col>
            <Col xs={24} sm={12} md={8}>
              <label style={{ fontSize: 12, color: '#666', display: 'block', marginBottom: 4 }}>Birim</label>
              <Select mode="multiple" style={{ width: '100%' }} placeholder="Birim seçin..." options={birimFilterOptions} value={filtreBirimIds} onChange={setFiltreBirimIds} allowClear />
            </Col>
            <Col xs={24} sm={12} md={8}>
              <label style={{ fontSize: 12, color: '#666', display: 'block', marginBottom: 4 }}>Birim Fiyat</label>
              <Space.Compact style={{ width: '100%' }}>
                <Select style={{ width: '45%' }} placeholder="Koşul" value={filtreFiyatOp} onChange={setFiltreFiyatOp} allowClear options={karsilastirmaOpsiyonlari} />
                <InputNumber {...paraInputProps} style={{ width: '55%' }} placeholder="Fiyat" value={filtreFiyatDeger} onChange={setFiltreFiyatDeger} min={0} />
              </Space.Compact>
            </Col>
            <Col xs={24} sm={12} md={8}>
              <label style={{ fontSize: 12, color: '#666', display: 'block', marginBottom: 4 }}>Birim Dönüşümü</label>
              <Radio.Group value={filtreDonusum} onChange={(e) => setFiltreDonusum(e.target.value)} optionType="button" buttonStyle="solid" options={[
                { value: 'tumu', label: 'Tümü' },
                { value: 'var', label: 'Var' },
                { value: 'yok', label: 'Yok' },
              ]} />
            </Col>
          </Row>
        </Card>
      )}]} />

      {filtreAktifMi && (
        <Card size="small" style={{ marginBottom: 16, background: '#fafafa' }}>
          <Row gutter={16}>
            <Col xs={12} sm={8} md={6}><Statistic title="Malzeme Sayısı" value={toplamlar.adet} /></Col>
          </Row>
        </Card>
      )}

      <Table
        columns={columns}
        dataSource={filteredData}
        rowKey="id"
        loading={loading}
        pagination={{
          defaultPageSize: 10,
          pageSizeOptions: ['10', '20', '50'],
          showSizeChanger: true,
          showTotal: (total, range) => `${range[0]}-${range[1]} / ${total} kayıt`,
        }}
      />

      <Modal title={editingRecord ? 'Malzeme Düzenle' : 'Yeni Malzeme'} open={modalOpen} onCancel={() => { setModalOpen(false); setEditingRecord(null); setBirimKilitli(false); setBirimKullanim(null); form.resetFields(); }} onOk={() => form.submit()}>
        <Form form={form} layout="vertical" onFinish={handleSubmit} initialValues={{}}>
          <Form.Item name="ad" label="Ad" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
<Form.Item name="aciklama" label="Açıklama">
            <Input.TextArea rows={2} />
          </Form.Item>
          <Form.Item
            name="birim_id"
            label="Birim"
            extra={birimKilitli && birimKullanim
              ? (() => {
                  const idByAd = Object.fromEntries(birimlerRaw.map(b => [b.ad, b.id]));
                  const donusumGruplari = [
                    [idByAd['kg'], idByAd['gr']],
                    [idByAd['lt'], idByAd['ml']],
                  ].map(g => g.filter(Boolean));
                  const mevcutBirimId = editingRecord?.birim_id;
                  const grupVar = donusumGruplari.some(g => g.includes(mevcutBirimId));
                  if (grupVar) {
                    return `Bu malzemeye bağlı kayıtlar var (${birimKullanim.alis_kalemleri} alış, ${birimKullanim.urun_recete_kalemleri} reçete). Sadece kg↔gr veya lt↔ml dönüşümüne izin verilir, tüm kayıtlar otomatik dönüştürülür.`;
                  }
                  return `Bu malzemeye bağlı kayıtlar var (${birimKullanim.alis_kalemleri} alış, ${birimKullanim.urun_recete_kalemleri} reçete). Birim değiştirilemez.`;
                })()
              : undefined}
          >
            <Select
              options={(() => {
                if (!birimKilitli) return birimler;
                const idByAd = Object.fromEntries(birimlerRaw.map(b => [b.ad, b.id]));
                const donusumGruplari = [
                  [idByAd['kg'], idByAd['gr']],
                  [idByAd['lt'], idByAd['ml']],
                ].map(g => g.filter(Boolean));
                const mevcutBirimId = editingRecord?.birim_id;
                const grup = donusumGruplari.find(g => g.includes(mevcutBirimId));
                if (grup) return birimler.filter(b => grup.includes(b.value));
                return birimler.filter(b => b.value === mevcutBirimId);
              })()}
              disabled={birimKilitli && (() => {
                const idByAd = Object.fromEntries(birimlerRaw.map(b => [b.ad, b.id]));
                const donusumGruplari = [
                  [idByAd['kg'], idByAd['gr']],
                  [idByAd['lt'], idByAd['ml']],
                ].map(g => g.filter(Boolean));
                const mevcutBirimId = editingRecord?.birim_id;
                return !donusumGruplari.some(g => g.includes(mevcutBirimId));
              })()}
            />
          </Form.Item>
          {editingRecord && (
            <Button
              type="dashed"
              block
              onClick={() => { setDonusumMalzeme(editingRecord); setDonusumOpen(true); }}
            >
              Birim Dönüşümleri
            </Button>
          )}
        </Form>
      </Modal>

      <MalzemeDetayModal
        open={detayOpen}
        malzeme={detayMalzeme}
        onClose={() => { setDetayOpen(false); setDetayMalzeme(null); }}
        onSaved={fetchDonusumluMalzemeler}
      />

      <BirimDonusumModal
        open={donusumOpen}
        malzeme={donusumMalzeme}
        birimler={birimler}
        birimlerRaw={birimlerRaw}
        onClose={() => { setDonusumOpen(false); setDonusumMalzeme(null); }}
        onSaved={fetchDonusumluMalzemeler}
      />
    </div>
  );
}

export default Malzemeler;